// Forked from services/cases.ts at `cases-pre-sr-fork` (Service Requests start as a copy of Cases).
// Comments below were written for Cases and still describe this file until the two diverge.
import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import * as errors from './errors'
import { getContactsForCompanyIdPreProcessed } from './companies'
import { getContactsForSupplierIdPreProcessed } from './suppliers'
import { getContactsPreProcessed } from './contacts'
import { getCompanyMSService } from './companyMSServices'
import type {
  TEntityData,
  TEntityId,
  TEntityVersion,
  TOmitNamed,
} from '~/types/models'
import type {
  IServiceRequest,
  IServiceRequestOverview,
} from '~/types/serviceRequest/models'
import { Contact } from '~/cynqueModules/dataObjects/Contact'
import type { ICompanyMSService } from '~/types/company/models'
import type { TContact } from '~/types/contact/models'
import { groupContactsByParent } from '~/cynqueModules/groupContactsByParent'

const apiEntityPath = '/api/servicerequests'

/**
 * The contacts a case may be raised by, for whichever anchor it has.
 *
 * A case anchored to a SUPPLIER offers that supplier's contacts (pass
 * supplierId alone). One anchored to an ACTIVE SERVICE offers the service's
 * company's contacts AND the service's supplier's contacts (pass both) --
 * whoever reported it could sit on either side.
 *
 * ⚠ THE `Contact()` RE-RUN IS THE POINT, not tidiness. The two getters above
 * enrich with `CompanyContact()` / `SupplierContact()`, whose `namePlusBiz` is
 * a DIFFERENT format from the one this picker labels by -- and
 * SupplierContact's is broken outright: `SupplierContact.ts:37` applies a unary
 * `+` to a phone string, so every supplier contact's namePlusBiz ends in the
 * literal "NaN". `Contact()` rebuilds it as "Name | parent | title", which is
 * what the Service Request contact field showed before it was scoped and what every other
 * contact picker shows. Same reasoning, same one-line fix, as
 * getAddableContactsForOpportunityMasterServicePreProcessed.
 *
 * Either id may be absent -- no anchor chosen, or an anchor chosen but its
 * entity not yet picked -- and an absent one contributes no contacts rather
 * than an unfiltered list.
 */
export async function getContactsForServiceRequestAnchorPreProcessed(
  scope: IServiceRequestContactScope
) {
  return (await getContactGroupsForServiceRequestAnchor(scope)).flat()
}

/** The ids a service request's contacts are scoped by; either may be absent. */
interface IServiceRequestContactScope {
  companyId?: TEntityId
  supplierId?: TEntityId
}

/**
 * getContactsForServiceRequestAnchorPreProcessed's two lists, kept apart: the
 * company's contacts, then the supplier's.
 */
async function getContactGroupsForServiceRequestAnchor({
  companyId,
  supplierId,
}: IServiceRequestContactScope): Promise<TContact[][]> {
  const [companyContacts, supplierContacts] = await Promise.all([
    companyId
      ? getContactsForCompanyIdPreProcessed(companyId)
      : Promise.resolve([]),
    supplierId
      ? getContactsForSupplierIdPreProcessed(supplierId)
      : Promise.resolve([]),
  ])
  return [
    companyContacts.map((contact) => Contact(contact)),
    supplierContacts.map((contact) => Contact(contact)),
  ]
}

/**
 * The contacts Send Email's To / cc / bcc offer on a stored service request,
 * by its anchor -- which, once stored, IS whichever FK is set:
 *
 *  - a SERVICE: its company's and its supplier's contacts;
 *  - a SUPPLIER: that supplier's contacts;
 *  - neither (the Other anchor, or awaiting triage): EVERY contact.
 *
 * The same lists the form's Contact picker offers for each anchor
 * (modules/serviceRequest.ts), so the two agree on who a request concerns.
 *
 * ⚠ Neither falls back to every contact rather than none, as the Other anchor
 * does: an empty To would leave a request nobody can be emailed from except by
 * typing. Likewise a service that cannot be read.
 *
 * ⚠ The service may be `{ id, label }` and nothing else (a request stored by
 * an older API release): the embedded entity is used when it is there, and
 * fetched when it is not.
 */
export async function getServiceRequestReplyContacts(
  serviceRequest: Partial<IServiceRequest> | null | undefined
) {
  const scope = await resolveServiceRequestReplyScope(serviceRequest)
  return scope
    ? await getContactsForServiceRequestAnchorPreProcessed(scope)
    : await getContactsPreProcessed()
}

/**
 * getServiceRequestReplyContacts's contacts, kept in groups for Send Email's
 * To / cc / bcc, which shows them apart: the company's, then the supplier's.
 * Every contact -- no anchor to scope by -- is grouped the same way by whom
 * each belongs to (groupContactsByParent). Groups may be empty.
 */
export async function getServiceRequestReplyContactGroups(
  serviceRequest: Partial<IServiceRequest> | null | undefined
): Promise<TContact[][]> {
  const scope = await resolveServiceRequestReplyScope(serviceRequest)
  if (scope) {
    return await getContactGroupsForServiceRequestAnchor(scope)
  }
  return groupContactsByParent((await getContactsPreProcessed()) as TContact[])
}

// What getServiceRequestReplyContacts scopes by -- or null, for every contact.
async function resolveServiceRequestReplyScope(
  serviceRequest: Partial<IServiceRequest> | null | undefined
): Promise<IServiceRequestContactScope | null> {
  const fn = `[services/serviceRequests.ts] ${resolveServiceRequestReplyScope.name} |`
  const msService = serviceRequest?.companyMSService as
    | ICompanyMSService
    | null
    | undefined
  if (msService?.id) {
    let resolved = msService
    if (!resolved.masterService) {
      try {
        resolved = await getCompanyMSService(msService.id)
      } catch (e) {
        // Best effort: a full contact list is better than a dialog that will
        // not open.
        console.error(`${fn} could not read service '${msService.id}':`, e)
        return null
      }
    }
    return {
      companyId: resolved?.masterService?.parent?.id,
      supplierId: resolved?.supplier?.id,
    }
  }
  if (serviceRequest?.supplier?.id) {
    return { supplierId: serviceRequest.supplier.id }
  }
  return null
}

export async function getServiceRequests({ apiPath = apiEntityPath } = {}) {
  const callingMn = getServiceRequests.name
  return httpGet<IServiceRequest[]>({ callingMn, apiPath })
}

export async function getServiceRequest(id: TEntityId) {
  const callingMn = getServiceRequest.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<IServiceRequest>({ callingMn, apiPath })
}

export async function getServiceRequestOverview(id: TEntityId) {
  const callingMn = getServiceRequestOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  return httpGet<IServiceRequestOverview>({ callingMn, apiPath })
}

export async function createServiceRequest(entity: IServiceRequest) {
  const callingMn = createServiceRequest.name
  const apiPath = apiEntityPath
  type TServiceRequestNew = TOmitNamed<IServiceRequest, 'hrid'>
  return httpPost<TServiceRequestNew>({ callingMn, apiPath, entity })
}

export async function updateServiceRequest(
  entityData: Partial<IServiceRequest>,
  id: TEntityId
) {
  const callingMn = updateServiceRequest.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialIServiceRequest = Partial<IServiceRequest>
  return httpPatchMerge<TPartialIServiceRequest>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteServiceRequest(entityData: IServiceRequest) {
  const callingMn = deleteServiceRequest.name
  const apiPath = apiEntityPath
  return httpDelete<IServiceRequest>({
    callingMn,
    apiPath,
    entityData,
  })
}

/** A case named in a merge: its id and the version the client last read. */
export interface IServiceRequestMergeParty {
  id: TEntityId
  version: TEntityVersion
}

/**
 * Merge `source` INTO `survivor` (cynque-api-server #1658):
 * POST /api/servicerequests/{survivor-id}/merges with
 * `{ version: <survivor's>, source: { id, version } }`. Returns the survivor.
 *
 * The source's notes, documents, mail, activities and links move to the
 * survivor; the source is kept, status Merged, `mergedInto` the survivor. The
 * survivor's own fields win. One transaction, so a refusal moves nothing:
 *  - 400: source and survivor are the same case;
 *  - 404: either case is gone;
 *  - 409: either case is stale (someone edited it) or already merged.
 * serviceRequestMergeErrorMessage turns each into what to tell the user.
 *
 * Not undoable: the server keeps no record of which children moved.
 */
export async function mergeServiceRequests({
  survivor,
  source,
}: {
  survivor: IServiceRequestMergeParty
  source: IServiceRequestMergeParty
}) {
  const callingMn = mergeServiceRequests.name
  const apiPath = `${apiEntityPath}/${survivor.id}/merges`
  return (await httpPost({
    callingMn,
    apiPath,
    entity: {
      version: survivor.version,
      source: { id: source.id, version: source.version },
    } as unknown as TEntityData,
  })) as unknown as IServiceRequest
}

/** The title the API gives the 409 for a case that is already merged. */
export const SERVICE_REQUEST_ALREADY_MERGED_TITLE =
  'Service Request already merged'

/**
 * What to tell the user when mergeServiceRequests throws. httpPost types EVERY 409 as
 * UpdateConflictError, so the already-merged one is told apart by its title.
 */
export function serviceRequestMergeErrorMessage(error: unknown): string {
  if (error instanceof errors.UpdateConflictError) {
    const title = (error.body as Record<string, unknown> | null)?.title
    return title === SERVICE_REQUEST_ALREADY_MERGED_TITLE
      ? 'One of these service requests has already been merged. Reload the list, then try again.'
      : 'One of these service requests was changed by someone else. Reload the list, then try again.'
  }
  if (error instanceof errors.APIUnhandledStatus && error.statusCode === 404) {
    return 'One of these service requests no longer exists. Reload the list.'
  }
  return `Merge failed: ${
    error instanceof Error ? error.message : 'unknown error'
  }.`
}
